import { Button, Form, Header } from 'semantic-ui-react';

import {
    SYSTEM_TYPE_EXISTING,
    SYSTEM_TYPE_NEW,
} from '../../../../Constants/gen1Constants';
import RepeaterDetailsTable from '../RepeaterDetailsTable';

/**
 * 購入済み（IncludePurchasedRepeaters）向け Repeater Operations + Details Section
 * @param {{
 *   gen1Order: Object,
 *   otherSystemId: string,
 *   addRepeater: boolean,
 *   validationErrors: Object,
 *   onFormChange: (evn: object, data: object) => void,
 *   onAddRepeaterRowClick: () => void,
 *   onRepeaterLineChange: (lineSequenceNumber: number, field: string, value: string) => void,
 *   onDeleteManualRepeaterRow: (lineSequenceNumber: number) => void,
 *   onDeleteSearchRepeaterRow: (lineSequenceNumber: number) => void,
 *   onDeleteRemoveRepeaterRow: (lineSequenceNumber: number) => void,
 *   onDeleteExchangeRepeaterPair: (pairId: number) => void,
 * }} props
 */
export default function PurchasedRepeaterOperationsSection({
    gen1Order,
    otherSystemId = '',
    addRepeater,
    validationErrors = {},
    onFormChange,
    onAddRepeaterRowClick,
    onRepeaterLineChange,
    onDeleteManualRepeaterRow,
    onDeleteSearchRepeaterRow,
    onDeleteRemoveRepeaterRow,
    onDeleteExchangeRepeaterPair,
}) {
    const item = gen1Order?.Gen1TrunkingOrderItem ?? {};
    const repeaterLines = gen1Order?.Gen1TrunkingOrderRepeaterSerials ?? [];
    const systemType = gen1Order?.SystemType ?? '';
    const isNewSystem = systemType === SYSTEM_TYPE_NEW;
    const showAddRepeaterCheckbox = !isNewSystem;
    const addRepeaterSectionActive = isNewSystem || addRepeater;
    const showExchangeRepeater = (item.ExchangeRepeaterPairQty ?? 0) > 0;
    const showRemoveRepeater = (item.RemoveRepeaterQty ?? 0) > 0;
    const hasAddRepeaterOperationsError =
        !!validationErrors.addRepeaterOperations;
    const hasRepeaterOperationsError = !!validationErrors.repeaterOperations;
    /** Existing: Add Repeater ブロック（Checkbox + 数量） */
    const existingAddSectionError =
        hasAddRepeaterOperationsError || hasRepeaterOperationsError;
    /** New System: 91800/91810 Input へ addRepeaterOperations 赤枠 */
    const addOpsErrorOnQtyFields =
        !showAddRepeaterCheckbox && hasAddRepeaterOperationsError;
    const repeaterQtyFieldError =
        !!validationErrors.repeaterQty || addOpsErrorOnQtyFields;
    const multiSiteQtyFieldError =
        !!validationErrors.multiSiteNetworkingQty || addOpsErrorOnQtyFields;

    return (
        <>
            <Header
                as="h4"
                content="Repeater Operations"
                style={{ marginTop: 24 }}
            />
            <p
                style={{
                    color: 'rgba(0,0,0,0.65)',
                    fontSize: '0.9em',
                    marginBottom: 12,
                    maxWidth: 720,
                }}
            >
                {isNewSystem
                    ? 'Add repeaters via Sales Order Number (SO#) search or Add Row, then enter Multi-site quantity if needed. Exchange and Remove are not available on New System.'
                    : 'Add repeaters: check Add Repeater and set quantities, then register models from Sales Order Number (SO#) search or Add Row. Exchange / Remove: enable the operation and set Quantity (pairs for exchange).'}
            </p>
            {(showAddRepeaterCheckbox || addRepeaterSectionActive) && (
                <Form.Field
                    error={
                        showAddRepeaterCheckbox
                            ? existingAddSectionError
                            : false
                    }
                >
                    {showAddRepeaterCheckbox && (
                        <Form.Group>
                            <Form.Checkbox
                                label="Add Repeater"
                                name="addRepeater"
                                checked={addRepeater}
                                onChange={onFormChange}
                            />
                        </Form.Group>
                    )}
                    {addRepeaterSectionActive && (
                        <Form.Group widths="equal">
                            <Form.Input
                                label="KSGPG91800 Repeater Trunking Feature"
                                type="number"
                                name="RepeaterQty"
                                value={item.RepeaterQty ?? 0}
                                disabled
                                error={repeaterQtyFieldError}
                            />
                            <Form.Input
                                type="number"
                                min={0}
                                name="MultiSiteNetworkingQty"
                                label="KSGPG91810 Multi_site Networking Feature"
                                value={item.MultiSiteNetworkingQty ?? 0}
                                onChange={onFormChange}
                                error={multiSiteQtyFieldError}
                            />
                        </Form.Group>
                    )}
                </Form.Field>
            )}
            {systemType === SYSTEM_TYPE_EXISTING && (
                <Form.Field error={hasRepeaterOperationsError}>
                    <Form.Field>
                        <Form.Checkbox
                            label="Exchange Repeater"
                            name="ExchangeRepeater"
                            checked={showExchangeRepeater}
                            onChange={onFormChange}
                        />
                    </Form.Field>
                    {showExchangeRepeater && (
                        <Form.Group>
                            <Form.Field
                                width={4}
                                required
                                error={
                                    !!validationErrors.exchangeRepeaterQuantity
                                }
                            >
                                <label>Quantity</label>
                                <Form.Input
                                    type="number"
                                    min={0}
                                    name="ExchangeRepeaterPairQty"
                                    placeholder="Enter quantity"
                                    value={item.ExchangeRepeaterPairQty ?? 0}
                                    onChange={onFormChange}
                                    error={
                                        !!validationErrors.exchangeRepeaterQuantity
                                    }
                                />
                            </Form.Field>
                            <Form.Field
                                width={4}
                                required
                                error={!!validationErrors.otherSystemId}
                            >
                                <label>Other System ID</label>
                                <Form.Input
                                    name="otherSystemId"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder="Enter System ID (numbers only)"
                                    value={otherSystemId}
                                    onChange={onFormChange}
                                    error={!!validationErrors.otherSystemId}
                                />
                            </Form.Field>
                        </Form.Group>
                    )}
                    <Form.Field>
                        <Form.Checkbox
                            label="Remove Repeater"
                            name="RemoveRepeater"
                            checked={showRemoveRepeater}
                            onChange={onFormChange}
                        />
                    </Form.Field>
                    {showRemoveRepeater && (
                        <Form.Field
                            width={4}
                            required
                            error={!!validationErrors.removeRepeaterQuantity}
                        >
                            <label>Quantity</label>
                            <Form.Input
                                type="number"
                                min={0}
                                name="RemoveRepeaterQty"
                                value={item.RemoveRepeaterQty ?? 0}
                                onChange={onFormChange}
                                error={
                                    !!validationErrors.removeRepeaterQuantity
                                }
                            />
                        </Form.Field>
                    )}
                </Form.Field>
            )}

            <Header
                as="h4"
                style={{
                    marginTop: 24,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <span>Repeater Details</span>
                <Button
                    size="small"
                    icon="plus"
                    content="Add Row"
                    onClick={onAddRepeaterRowClick}
                />
            </Header>
            {repeaterLines.length > 0 ? (
                <RepeaterDetailsTable
                    repeaterLines={repeaterLines}
                    systemType={systemType}
                    systemId={gen1Order?.SystemId ?? ''}
                    otherSystemId={otherSystemId}
                    addRepeater={addRepeaterSectionActive}
                    repeaterTrunkingQty={item.RepeaterQty ?? 0}
                    exchangeRepeater={showExchangeRepeater}
                    exchangeRepeaterQuantity={item.ExchangeRepeaterPairQty ?? 0}
                    removeRepeater={showRemoveRepeater}
                    removeRepeaterQuantity={item.RemoveRepeaterQty ?? 0}
                    validationErrors={validationErrors}
                    onRepeaterLineChange={onRepeaterLineChange}
                    onDeleteManualRepeaterRow={onDeleteManualRepeaterRow}
                    onDeleteSearchRepeaterRow={onDeleteSearchRepeaterRow}
                    onDeleteRemoveRepeaterRow={onDeleteRemoveRepeaterRow}
                    onDeleteExchangeRepeaterPair={onDeleteExchangeRepeaterPair}
                />
            ) : null}
        </>
    );
}
